import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import PageHeaderWithTabs from '../../components/ui/PageHeaderWithTabs';
import { listLicences } from '../../services/licences';
import { listQuotations } from '../../services/quotations';

const MyApprovalQueue = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('licence'); // 'licence' | 'quotation'
  const [licences, setLicences] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
  const [licenceTabCount, setLicenceTabCount] = useState(0);
  const [quotationTabCount, setQuotationTabCount] = useState(0);

  // Fetch tab counts (awaiting approval) for red dot
  useEffect(() => {
    let cancelled = false;
    const fetchTabCounts = async () => {
      try {
        const [licRes, quoRes] = await Promise.all([
          listLicences({ myApprovalQueue: true, page: 1, limit: 1 }),
          listQuotations({ myApprovalQueue: true, page: 1, limit: 1 })
        ]);
        if (!cancelled) {
          setLicenceTabCount(licRes?.pagination?.total ?? 0);
          setQuotationTabCount(quoRes?.pagination?.total ?? 0);
        }
      } catch (e) {
        if (!cancelled) {
          setLicenceTabCount(0);
          setQuotationTabCount(0);
        }
      }
    };
    fetchTabCounts();
    return () => { cancelled = true; };
  }, []);

  // Load licences/quotations assigned to current user as approver
  const loadLicences = async () => {
    try {
      setLoading(true);
      setError(null);
      let response;

      if (activeTab === 'licence') {
        response = await listLicences({
          myApprovalQueue: true,
          search: searchTerm,
          page: currentPage,
          limit: 10
        });
      } else {
        response = await listQuotations({
          myApprovalQueue: true,
          search: searchTerm,
          page: currentPage,
          limit: 10
        });
      }

      setLicences(response.data || []);
      setPagination(response.pagination || { total: 0, page: 1, limit: 10 });
      const total = response.pagination?.total ?? 0;
      if (activeTab === 'licence') setLicenceTabCount(total);
      else setQuotationTabCount(total);
    } catch (err) {
      console.error('Failed to load approval queue:', err);
      setError('Failed to load approval queue');
      setLicences([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLicences();
  }, [currentPage, searchTerm, activeTab]);

  const handleSearch = (e) => {
    if (e) {
      e.preventDefault();
    }
    setCurrentPage(1);
    loadLicences();
  };

  const columns = [
    { header: 'Ref Number', accessor: 'refNumber' },
    { header: 'Organisation', accessor: 'organization' },
    { header: 'Licence Name', accessor: 'licenceName' },
    { header: 'Status', accessor: 'status' },
    { header: 'Customer Action', accessor: 'customerActionKey', render: (val) => val ? <span className="px-2.5 py-1 inline-flex text-xs font-medium rounded-full bg-red-500
       text-[#060202]">{val}</span> : <span className="text-gray-400">—</span> },
    { header: 'Reviewed', accessor: 'reviewedDate' },
    { header: 'Actions', accessor: 'actions' }
  ];

  const tableData = licences.map(item => ({
    refNumber: item.refNumber || 'N/A',
    organization: item.org?.name || 'N/A',
    licenceName: item.coaId?.name || 'N/A',
    status: item.status || 'N/A',
    customerActionKey: item.customerAction?.key || '',
    reviewedDate: item.reviewedAt ? new Date(item.reviewedAt).toLocaleDateString('en-GB') : 'N/A',
    actions: 'view',
    id: item._id || item.id,
    hasUnreadActivity: item.hasUnreadActivity || false
  }));

  const handleRowAction = (action, row) => {
    if (action === 'view') {
      if (activeTab === 'licence') {
        navigate(`/licences/${row.id}`);
      } else {
        navigate(`/quotations/${row.id}`);
      }
    }
  };

  const getStatusBadge = (status, hasUnreadActivity) => {
    const statusConfig = {
      'Awaiting Review': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Awaiting Review' },
      'Under Review': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
      'Re-review': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Re-review' },
      'Under Review – Additional Info Required': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review – Additional Info Required' },
      'Reviewed': { bg: 'bg-green-100', text: 'text-green-800', label: 'Reviewed' },
      'Approved': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Approved' },
      'Rejected': { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig['Awaiting Review'];
    // Base attention statuses before approval
    const baseAttentionStatuses = [
      'Awaiting Review',
      'Under Review',
      'Re-review',
      'Under Review – Additional Info Required'
    ];
    // Needs attention if pre-approval OR there is unread activity (including after approval)
    const needsAttention = baseAttentionStatuses.includes(status) || hasUnreadActivity;
    return (
      <div className="flex items-center gap-2">
        {needsAttention && (
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
        )}
        <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
          {config.label}
        </span>
      </div>
    );
  };

  const tabs = [
    { name: 'licence', label: 'Licence', count: licenceTabCount },
    { name: 'quotation', label: 'Quotations', count: quotationTabCount }
  ];

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setCurrentPage(1);
  };

  return (
    <div className="bg-[#fafafa] min-h-screen p-6">
      <div className="max-w-[1920px] mx-auto">
        {/* Header with Tabs and Search */}
        <PageHeaderWithTabs
          title="My Approval Queue"
          description="Requests reviewed and ready for your approval"
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={handleSearch}
          searchPlaceholder="Search by ref number, organisation, or name..."
        />

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mx-auto"></div>
            <p className="mt-4 text-[#73827c]">Loading...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
            <button
              onClick={loadLicences}
              className="mt-4 px-4 py-2 bg-emerald-400 text-white rounded-lg hover:bg-emerald-500"
            >
              Retry
            </button>
          </div>
        ) : licences.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-[12px] border border-[#f2f2f2]">
            <p className="text-[#73827c]">
              {activeTab === 'licence'
                ? 'No licence requests in your approval queue'
                : 'No quotations in your approval queue'}
            </p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={tableData}
            loading={loading}
            onRowAction={handleRowAction}
            renderCell={(accessor, row) => {
              if (accessor === 'status') {
                return getStatusBadge(row.status, row.hasUnreadActivity);
              }
              if (accessor === 'actions') {
                const detailPath = activeTab === 'licence' 
                  ? `/licences/${row.id}` 
                  : `/quotations/${row.id}`;
                const editPath = activeTab === 'licence'
                  ? `/edit-license-request/${row.id}`
                  : null;
                return (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate(detailPath)}
                      className="text-emerald-400 hover:text-emerald-500 text-sm font-medium"
                    >
                      View
                    </button>
                    {activeTab === 'licence' && (
                      <button
                        onClick={() => navigate(editPath)}
                        className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                );
              }
              return <span className="text-sm text-gray-900">{row[accessor]}</span>;
            }}
            showPagination={true}
            currentPage={currentPage}
            pagination={pagination}
            onPageChange={setCurrentPage}
            rowsPerPage={10}
          />
        )}
      </div>
    </div>
  );
};

export default MyApprovalQueue;


